import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { Box, Flex, Spacer, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '../..';
import { BlackLogo, CameraOffIcon, CameraOnIcon, MicOffIcon, MicOnIcon } from '../../../images';
import {
  SessionType,
  changeSessionType,
  closeModal,
  openModal,
  Modals,
  updateSettings,
} from '../../../redux/reducers';
import { useLocalAudioTrack, useLocalVideoTrack } from '../../../twilio/hooks';
import VolumeControl from './volumeControl';
import FooterMenu from './footerMenu';
import { isChatAdmin } from '../../../utils/user/user';

const Logo = () => {
  const { t } = useTranslation();
  return (
    <Flex alignItems="center">
      <BlackLogo style={{ transform: 'scale(0.6' }} />
      <Text color="white" fontSize="xl" fontWeight="bold">
        {t('chabla').toUpperCase()}
      </Text>
    </Flex>
  );
};

const formatDuration = (seconds) => {
  let hours = Math.floor(seconds / 3600);
  let minutes = Math.floor((seconds - hours * 3600) / 60);
  seconds = Math.floor(seconds - hours * 3600 - minutes * 60);

  if (hours < 10) {
    hours = '0' + hours;
  }
  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  if (seconds < 10) {
    seconds = '0' + seconds;
  }

  if (hours === '00') {
    return `${minutes}:${seconds}`;
  } else {
    return `${hours}:${minutes}:${seconds}`;
  }
};

const Duration = (props) => {
  const now = +new Date();
  const startTime = useSelector((state) => state.activeSession.startTime);
  const durationInSeconds = (now - startTime) / 1000;
  const duration = startTime ? formatDuration(durationInSeconds) : '--:--';

  // rerender every second
  const [, setTime] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Box mx={2} {...props}>
      {duration}
    </Box>
  );
};

const Video = (props) => {
  const dispatch = useDispatch();
  const sessionType = useSelector((state) => state.activeSession.type);
  const { t } = useTranslation();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const ownVideoTrack = useLocalVideoTrack();
  const isVideoOn = ownVideoTrack && ownVideoTrack.isEnabled;
  const toggleVideo = useCallback(() => {
    if (!ownVideoTrack) {
      return;
    }
    if (ownVideoTrack.isEnabled) {
      ownVideoTrack.disable();
      dispatch(updateSettings({ cameraInUse: false }));
    } else {
      ownVideoTrack.enable();
      dispatch(updateSettings({ cameraInUse: true }));
    }
    forceUpdate();
  }, [ownVideoTrack]);

  if (sessionType === SessionType.TEXT) {
    return null;
  }

  return (
    <Flex mx={2} {...props}>
      {t('footer.video')}
      <Button variant="link" onClick={toggleVideo}>
        {isVideoOn ? <CameraOnIcon /> : <CameraOffIcon />}
      </Button>
    </Flex>
  );
};

const Mic = (props) => {
  const { t } = useTranslation();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const ownAudioTrack = useLocalAudioTrack();
  const isMicOn = useSelector(() => ownAudioTrack && ownAudioTrack.isEnabled);
  const toggleMute = useCallback(() => {
    if (!ownAudioTrack) {
      return;
    }
    if (ownAudioTrack.isEnabled) {
      ownAudioTrack.disable();
    } else {
      ownAudioTrack.enable();
    }
    forceUpdate();
  }, [ownAudioTrack]);

  return (
    <Flex alignItems="center" mx={2} {...props}>
      {t('footer.mute')}
      <Button variant="link" onClick={toggleMute}>
        {isMicOn ? <MicOnIcon /> : <MicOffIcon />}
      </Button>
    </Flex>
  );
};

const ChangeSessionType = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const sessionType = useSelector((state) => state.activeSession.type);
  const { userData } = useSelector((state) => state.user);
  const canChangeSessionType = isChatAdmin(userData); // && sessionType === SessionType.VIDEO;

  const onClick = useCallback(() => {
    dispatch(
      openModal(
        {
          headerText:
            sessionType === SessionType.VIDEO
              ? t('modal.changeSessionType.toText')
              : t('modal.changeSessionType.toVideo'),
          bodyText: '',
          leftButtonText: t('modal.cancel'),
          rightButtonText: t('modal.yes'),
          onLeftButtonClick: () => {
            dispatch(closeModal());
          },
          onRightButtonClick: () => {
            dispatch(closeModal());
            dispatch(
              changeSessionType(
                sessionType === SessionType.VIDEO ? SessionType.TEXT : SessionType.VIDEO
              )
            );
          },
        },
        Modals.CONFIRM
      )
    );
  }, [sessionType]);

  if (!canChangeSessionType) {
    return null;
  }

  return (
    <Button bg="primary" borderRadius="20px" fontWeight="400" {...props} onClick={onClick}>
      {sessionType === SessionType.VIDEO ? t('footer.changeToText') : t('footer.changeToVideo')}
    </Button>
  );
};

const EndSessionButton = ({ onEndSession }) => {
  const { t } = useTranslation();

  return (
    <Button bg="brand.60" color="white" my={6} fontWeight="700" onClick={onEndSession}>
      {t('footer.endSession')}
    </Button>
  );
};

EndSessionButton.propTypes = {
  onEndSession: PropTypes.func,
};

const SessionFooter = ({ onEndSession, ...containerProps }) => {
  return (
    <Flex
      color="brand.10"
      bg="secondary"
      direction="row"
      {...containerProps}
      alignItems="center"
      px={4}
      py={6}
      textAlign="center"
    >
      <Logo />
      <Duration flexGrow="0.4" />
      <Video flexGrow="0.2" />
      <Mic flexGrow="0.2" />
      <VolumeControl flexGrow="0.2" />
      <Spacer />
      <ChangeSessionType flexGrow="0.2" />
      <FooterMenu flexGrow="0.1" />
      <EndSessionButton onEndSession={onEndSession} />
    </Flex>
  );
};

SessionFooter.propTypes = {
  onEndSession: PropTypes.func,
};

export default SessionFooter;
